'use client';

import { useEffect, useId, useRef, useState } from 'react';
import styles from './CategoryInfoButton.module.css';

/**
 * Same hover / tap behavior as the leaderboard category info icon.
 * The message is enums.scoreCategoryInfo for that scoring category.
 */
export function CategoryInfoButton({
  label,
  content,
  iconSrc = '/live/player-detail/info-circle.svg',
  iconClassName,
  hideOnMobile = true,
}: {
  label: string;
  content: string;
  iconSrc?: string;
  iconClassName?: string;
  hideOnMobile?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [hoverCapable, setHoverCapable] = useState(true);
  const rootRef = useRef<HTMLDivElement>(null);
  const tipId = useId();

  useEffect(() => {
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    setHoverCapable(mq.matches);
    const onChange = (event: MediaQueryListEvent) => setHoverCapable(event.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (hoverCapable || !open) return;
    function onPointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, hoverCapable]);

  const hoverHandlers = hoverCapable
    ? {
        onMouseEnter: () => setOpen(true),
        onMouseLeave: () => setOpen(false),
      }
    : {};

  return (
    <div
      className={hideOnMobile ? `${styles.root} ${styles.hideOnMobile}` : styles.root}
      ref={rootRef}
      {...hoverHandlers}
    >
      <button
        type="button"
        className={styles.trigger}
        aria-label={label}
        aria-expanded={open}
        aria-describedby={open ? tipId : undefined}
        onClick={() => {
          if (!hoverCapable) setOpen((current) => !current);
        }}
      >
        <img src={iconSrc} className={iconClassName} alt="" width={24} height={24} />
      </button>
      {open && (
        <div id={tipId} role="tooltip" className={styles.tooltip}>
          {content}
        </div>
      )}
    </div>
  );
}
